import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import api from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { User, History, KeyRound, LogOut, Package, CheckCircle2, AlertCircle, Truck, Wrench, Calendar } from 'lucide-react';

const CustomerProfile = () => {
  const { customer, logout, loading } = useCustomerAuth();
  const [tab, setTab] = useState('history');

  if (loading) return <div className="p-10 text-neutral-500">Caricamento...</div>;
  if (!customer) return <Navigate to="/cliente/login" replace />;

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-6 py-8 fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-black brand-text">Il mio account</h1>
          <p className="text-sm text-neutral-500">{customer.name} · {customer.email}</p>
        </div>
        <button onClick={logout} className="flex items-center gap-2 px-4 py-2 border border-neutral-300 rounded text-sm font-bold hover:bg-neutral-50">
          <LogOut size={16} /> Logout
        </button>
      </div>

      <CustomerDiscountBanner customer={customer} />

      <div className="flex gap-1 border-b border-neutral-200 mb-6">
        {[
          { id: 'history', label: 'Storico Carrelli', icon: History },
          { id: 'profile', label: 'Profilo', icon: User },
          { id: 'password', label: 'Cambio Password', icon: KeyRound },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 ${tab === t.id ? 'border-[#d62828] text-[#d62828]' : 'border-transparent text-neutral-600 hover:text-neutral-900'}`}>
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'history' && <HistoryTab />}
      {tab === 'profile' && <ProfileTab customer={customer} />}
      {tab === 'password' && <PasswordTab scope="customer" />}
    </div>
  );
};

const CustomerDiscountBanner = ({ customer }) => {
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get('/categories').then((r) => setCats(r.data)).catch(() => setCats([])); }, []);

  const globalDiscount = customer.discount_all || 0;
  const catDiscounts = customer.discount_by_category || {};
  const hasCatDiscount = Object.keys(catDiscounts).length > 0;
  if (globalDiscount <= 0 && !hasCatDiscount) return null;

  const catName = (id) => (cats.find((c) => c.id === id)?.name) || 'categoria';

  return (
    <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg" data-testid="customer-discount-banner">
      <div className="flex items-start gap-3">
        <span className="text-3xl">🎉</span>
        <div className="flex-1">
          <h3 className="font-bold text-green-900">Sconti attivi sul tuo account</h3>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {globalDiscount > 0 && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-600 text-white text-sm font-bold rounded-full" data-testid="discount-all-badge">
                −{globalDiscount}% su tutto
              </span>
            )}
            {Object.entries(catDiscounts).map(([cid, pct]) => (
              <span key={cid} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm font-bold rounded-full">
                −{pct}% su {catName(cid)}
              </span>
            ))}
          </div>
          <p className="text-xs text-green-800 mt-2">Gli sconti verranno applicati alla richiesta di preventivo che ci invierai.</p>
        </div>
      </div>
    </div>
  );
};

const HistoryTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/customers/me/cart-history').then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-neutral-500">Caricamento...</div>;

  if (items.length === 0) {
    return (
      <div className="text-center py-16 bg-neutral-50 rounded-xl border border-neutral-200">
        <Package size={48} className="text-neutral-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-neutral-700">Nessuna richiesta inviata</h2>
        <p className="text-neutral-500 mt-1">Le richieste di preventivo che invii dal carrello saranno visibili qui.</p>
        <Link to="/noleggio" className="inline-block mt-6 px-5 py-2.5 bg-[#d62828] text-white rounded font-bold hover:bg-[#b41f1f]">Vai al catalogo</Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-500">{items.length} richieste inviate</p>
      {items.map((q) => {
        const total = q.items.reduce((s, i) => s + (i.price_per_day || 0) * i.quantity, 0);
        return (
          <div key={q.id} className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-neutral-200 bg-neutral-50 flex justify-between items-center flex-wrap gap-2">
              <div>
                <span className="text-xs font-bold text-neutral-500 uppercase tracking-wider">Richiesta #{q.id.slice(0, 8).toUpperCase()}</span>
                <p className="text-sm text-neutral-700 flex items-center gap-1 mt-0.5">
                  <Calendar size={12} /> {new Date(q.created_at).toLocaleString('it-IT')}
                </p>
              </div>
              <span className={`text-xs font-bold px-3 py-1 rounded-full ${q.status === 'new' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
                {q.status === 'new' ? 'In attesa risposta' : q.status}
              </span>
            </div>
            <div className="p-5">
              {(q.start_date || q.end_date) && (
                <p className="text-sm text-neutral-700 mb-3">Periodo: <strong>{q.start_date || '?'}</strong> → <strong>{q.end_date || '?'}</strong></p>
              )}
              <div className="space-y-2">
                {q.items.map((it, i) => (
                  <div key={it.product_id || it.product_slug || `${q.id}-${i}`} className="flex justify-between items-center text-sm border-b border-neutral-100 pb-2">
                    <div className="flex-1">
                      <span className="font-bold">{it.quantity}× </span>
                      <Link to={`/prodotto/${it.product_slug}`} className="hover:text-[#d62828]">{it.product_name}</Link>
                      {it.brand && <span className="text-xs text-neutral-500 ml-2">({it.brand})</span>}
                    </div>
                    {it.price_per_day > 0 && (
                      <div className="text-right text-xs text-neutral-600">
                        € {it.price_per_day.toFixed(2)}/g × {it.quantity}<br />
                        <strong className="text-neutral-900">€ {((it.price_per_day || 0) * it.quantity).toFixed(2)}</strong>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {total > 0 && (
                <div className="mt-3 pt-3 border-t border-neutral-200 flex justify-between font-bold">
                  <span>Totale stimato/giorno</span>
                  <span className="text-[#d62828]">€ {total.toFixed(2)}</span>
                </div>
              )}
              <div className="flex gap-2 mt-3 flex-wrap text-xs">
                {q.delivery_pickup && <span className="bg-blue-50 text-blue-800 px-2 py-1 rounded font-bold inline-flex items-center gap-1"><Truck size={12} /> Consegna richiesta</span>}
                {q.setup_teardown && <span className="bg-purple-50 text-purple-800 px-2 py-1 rounded font-bold inline-flex items-center gap-1"><Wrench size={12} /> Setup richiesto</span>}
              </div>
              {q.message && <p className="text-sm text-neutral-600 italic mt-3">"{q.message}"</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const ProfileTab = ({ customer }) => (
  <div className="bg-white border border-neutral-200 rounded-lg p-6">
    <h2 className="text-lg font-bold mb-4">Dati profilo</h2>
    <dl className="grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
      {[['Nome', customer.name], ['Email', customer.email], ['Telefono', customer.phone || '—'], ['Azienda', customer.company || '—']].map(([k, v]) => (
        <div key={k}>
          <dt className="text-xs uppercase tracking-wider text-neutral-500 font-bold">{k}</dt>
          <dd className="text-neutral-800 font-medium mt-1">{v}</dd>
        </div>
      ))}
    </dl>
  </div>
);

export const PasswordTab = ({ scope = 'customer' }) => {
  const { toast } = useToast();
  const [data, setData] = useState({ current_password: '', new_password: '', confirm: '' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (data.new_password.length < 6) { setError('Password minima 6 caratteri'); return; }
    if (data.new_password !== data.confirm) { setError('Le password non coincidono'); return; }
    setSubmitting(true);
    try {
      const path = scope === 'admin' ? '/admin/password/change-request' : '/customers/password/change-request';
      const r = await api.post(path, { current_password: data.current_password, new_password: data.new_password });
      setResult(r.data);
      setData({ current_password: '', new_password: '', confirm: '' });
      toast({ title: 'Email simulata inviata', description: 'Conferma il cambio password con il link mostrato.' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-6 max-w-md">
      <h2 className="text-lg font-bold mb-4">Cambia password</h2>
      <p className="text-xs text-neutral-500 mb-4">Per motivi di sicurezza, dopo aver inviato il modulo riceverai una mail di conferma (simulata in fase di test).</p>
      {error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm flex items-center gap-2"><AlertCircle size={16} /> {error}</div>}
      {result && (
        <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded text-green-800 space-y-2">
          <p className="text-sm flex items-start gap-2"><CheckCircle2 size={16} className="mt-0.5" /> {result.message}</p>
          {result.confirm_token && (
            <div className="p-3 bg-white border border-amber-300 rounded">
              <p className="text-xs text-amber-700 font-bold mb-2">✉️ EMAIL SIMULATA — inviata a {result.sent_to}</p>
              <Link to={`/conferma-cambio-password?token=${result.confirm_token}&scope=${scope}`} className="inline-block px-3 py-1.5 bg-[#d62828] text-white text-xs font-bold rounded hover:bg-[#b41f1f]">
                Conferma cambio password →
              </Link>
            </div>
          )}
        </div>
      )}
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="block text-sm font-bold mb-1">Password attuale</label>
          <input type="password" required value={data.current_password} onChange={(e) => setData({ ...data, current_password: e.target.value })} className="w-full px-4 py-2 border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-[#d62828]/30" />
        </div>
        <div>
          <label className="block text-sm font-bold mb-1">Nuova password</label>
          <input type="password" required value={data.new_password} onChange={(e) => setData({ ...data, new_password: e.target.value })} className="w-full px-4 py-2 border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-[#d62828]/30" />
        </div>
        <div>
          <label className="block text-sm font-bold mb-1">Conferma nuova password</label>
          <input type="password" required value={data.confirm} onChange={(e) => setData({ ...data, confirm: e.target.value })} className="w-full px-4 py-2 border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-[#d62828]/30" />
        </div>
        <button type="submit" disabled={submitting} className="px-5 py-2.5 bg-[#d62828] text-white font-bold rounded hover:bg-[#b41f1f] disabled:opacity-60">
          {submitting ? 'Invio...' : 'Richiedi cambio password'}
        </button>
      </form>
    </div>
  );
};

export default CustomerProfile;
